import React, { FC } from 'react'
import {
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'

import {
  DataTable,
  type DataTableColumn,
  EmptyState,
} from '@/apps/latest/components/portal/notion'
import { Tag } from '@/apps/latest/components/atoms/Tag'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'
import {
  SoapRule,
  soapSectionShortLabels,
} from '@/features/internal/soap-rules/types/soapRule'

interface Props {
  rules: SoapRule[] | undefined
  loading: boolean
  onEdit: (rule: SoapRule) => void
  onDelete: (rule: SoapRule) => void
}

const COLUMNS: DataTableColumn[] = [
  { key: 'kind', label: '種別', align: 'center' },
  { key: 'rule', label: 'ルール（上から順に適用）' },
  { key: 'actions', label: '', align: 'right' },
]

// ルールを自然文で表す (一覧はこの1文が主役。フィールドの羅列にしない)
export const describeRule = (rule: SoapRule): string => {
  if (rule.kind === 'add') {
    const section = rule.section ? soapSectionShortLabels[rule.section] : '指定'
    return `会話に「${rule.when_text}」が出たら、${section}欄に「${rule.then_text}」を追記`
  }
  if (!rule.then_text) {
    return `「${rule.when_text}」を削除`
  }
  return `「${rule.when_text}」を「${rule.then_text}」に置換`
}

export const SoapRuleTable: FC<Props> = ({
  rules,
  loading,
  onEdit,
  onDelete,
}) => {
  if (loading) {
    return (
      <Stack gap={1}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rectangular" height={48} />
        ))}
      </Stack>
    )
  }

  if (!rules || rules.length === 0) {
    return (
      <EmptyState
        title="まだルールが作成されていません"
        hint="「新規ルール」 ボタンから作成してください"
      />
    )
  }

  return (
    <DataTable columns={COLUMNS}>
      {rules.map((rule) => (
        <TableRow key={rule.id} hover>
          <TableCell align="center" sx={{ width: 72 }}>
            <Tag tone={rule.kind === 'add' ? 'info' : 'neutral'}>
              {rule.kind === 'add' ? '追記' : '置換'}
            </Tag>
          </TableCell>
          <TableCell>
            <Typography sx={PORTAL_TEXT.cellBody}>
              {describeRule(rule)}
            </Typography>
          </TableCell>
          <TableCell align="right" sx={{ width: 96 }}>
            <Stack direction="row" gap={0.5} justifyContent="flex-end">
              <Tooltip title="編集" arrow>
                <IconButton size="small" onClick={() => onEdit(rule)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="削除" arrow>
                <IconButton size="small" onClick={() => onDelete(rule)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </TableCell>
        </TableRow>
      ))}
    </DataTable>
  )
}
